import { createElement, useId, useRef, useState, type KeyboardEvent } from 'react';
import { MatSelectHost } from '@/kit/MatSelectHost';
import { SelectPanel } from '@/kit/SelectPanel';

export interface MatSelectOption<T> {
  value: T | null;
  label: string;
}

interface MatSelectFieldProps<T> {
  /** `<mat-label>`. */
  label: string;
  /** The model (`ngModel`); null shows the empty select. */
  value: T | null;
  options: readonly MatSelectOption<T>[];
  /** `(selectionChange)`, called after the model changed. */
  onChange: (value: T | null) => void;
  className?: string;
}

/**
 * `<mat-form-field appearance="fill"><mat-label/><mat-select [(ngModel)] (selectionChange)>` with `<mat-option>`s.
 * Behaves like Material's single select: an option whose value is null is the "clear" option (it is never shown as
 * selected; choosing it resets the model to null), re-choosing the current option changes nothing, and the arrow keys
 * change the value while the panel is closed.
 */
export function MatSelectField<T extends string | number>({
  label,
  value,
  options,
  onChange,
  className = '',
}: MatSelectFieldProps<T>) {
  const panelId = useId();
  const triggerRef = useRef<HTMLElement>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor !== null;
  const setOpen = (next: boolean) => setAnchor(next ? triggerRef.current : null);
  const [focused, setFocused] = useState(false);

  const selected =
    value === null ? undefined : options.find((option) => option.value !== null && option.value === value);
  const empty = selected === undefined;

  const choose = (option: MatSelectOption<T>) => {
    setOpen(false);
    if (option.value === null) {
      if (value !== null) {
        onChange(null);
      }
      return;
    }
    if (option.value !== value) {
      onChange(option.value);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (event.altKey) {
        setOpen(true);
        return;
      }
      const list = options.filter((option) => option.value !== null);
      if (list.length === 0) {
        return;
      }
      const at = selected ? list.indexOf(selected) : -1;
      const next = event.key === 'ArrowDown' ? Math.min(at + 1, list.length - 1) : Math.max(at - 1, 0);
      if (list[next] !== selected) {
        choose(list[next]);
      }
    }
  };

  const classes = [
    'mat-mdc-form-field',
    'mat-mdc-form-field-type-mat-select',
    'mat-form-field-appearance-fill',
    'mat-primary',
    (focused || open) && 'mat-focused',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return createElement(
    'mat-form-field',
    { className: classes },
    <>
      <div className="mat-mdc-text-field-wrapper mdc-text-field mdc-text-field--filled">
        <div className="mat-mdc-form-field-focus-overlay"></div>
        <div className="mat-mdc-form-field-flex">
          <div className="mat-mdc-form-field-infix">
            <label
              className={`mdc-floating-label mat-mdc-floating-label${empty && !(focused || open) ? '' : ' mdc-floating-label--float-above'}`}
            >
              {createElement('mat-label', {}, label)}
            </label>
            <MatSelectHost
              ref={triggerRef}
              className={`mat-mdc-select${empty ? ' mat-mdc-select-empty' : ''}`}
              role="combobox"
              tabIndex={0}
              aria-label={label}
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={open ? panelId : undefined}
              onClick={() => setOpen(true)}
              onKeyDown={onKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
            >
              <div className="mat-mdc-select-trigger">
                <div className="mat-mdc-select-value">
                  {selected && (
                    <span className="mat-mdc-select-value-text">
                      <span className="mat-mdc-select-min-line">{selected.label}</span>
                    </span>
                  )}
                </div>
                <div className="mat-mdc-select-arrow-wrapper">
                  <div className="mat-mdc-select-arrow">
                    <svg viewBox="0 0 24 24" width="24px" height="24px" focusable="false" aria-hidden="true">
                      <path d="M7 10l5 5 5-5z" />
                    </svg>
                  </div>
                </div>
              </div>
            </MatSelectHost>
          </div>
        </div>
        <div className="mdc-line-ripple"></div>
      </div>
      <div className="mat-mdc-form-field-subscript-wrapper mat-mdc-form-field-bottom-align">
        <div className="mat-mdc-form-field-hint-wrapper">
          <div className="mat-mdc-form-field-hint-spacer"></div>
        </div>
      </div>
      <SelectPanel
        open={open}
        anchorEl={anchor}
        onClose={() => setOpen(false)}
        className="home-select-panel"
        ariaLabel={label}
        exactWidth={false}
        options={options.map((option) => ({
          key: option.value === null ? '__null__' : String(option.value),
          label: option.label,
          selected: selected === option,
        }))}
        onSelect={(picked) => {
          const option = options.find((o) => (o.value === null ? '__null__' : String(o.value)) === picked.key);
          if (option) choose(option);
        }}
      />
    </>,
  );
}
